import {
  classifyWebStudioCachedState,
  type WebStudioCachedStateClassification,
} from "./bootConfig";
import {
  type WebStudioCloudApiClient,
  type WebStudioCloudApiResponseByOperation,
  type WebStudioFactsPlaneSnapshot,
  type WebStudioResponseIdentity,
  type WebStudioTarget,
  webStudioCloudApiOperations,
} from "./generatedClient";
import { makeWebStudioRequest } from "./transport";

export interface WebStudioScope {
  accountId: string;
  organizationId: string;
  projectId: string;
  workspaceId: string;
  environmentId: string;
  worldviewId?: string | undefined;
  lineageId?: string | undefined;
  activationId?: string | undefined;
  shellReference?: string | undefined;
  inspectorContext?: string | undefined;
  activityLens?: string | undefined;
  replaySubMode?: string | undefined;
  exportKind?: string | undefined;
  provenanceSeedId?: string | undefined;
  commandId?: string | undefined;
  supportSnapshotId?: string | undefined;
  mutationContextDigest?: string | undefined;
}

export type WebStudioBootSnapshot = WebStudioCloudApiResponseByOperation["webStudioBoot"];
export type WebStudioFactsPlane = WebStudioFactsPlaneSnapshot;
export type WebStudioBillingOverview = WebStudioCloudApiResponseByOperation["readBillingOverview"];

export interface WebStudioBootLoadOptions {
  signal?: AbortSignal;
  isCurrent?: (response: WebStudioBootSnapshot) => boolean;
  expectedGeneratedScopeDigest?: string;
  expectedRouteStateDigest?: string;
  expectedOperationContractIdentity?: string;
  expectedSnapshotIdentity?: string;
  now?: () => Date;
}

export class WebStudioStaleResponseError extends Error {
  constructor(
    message: string,
    readonly identity?: WebStudioResponseIdentity,
  ) {
    super(message);
    this.name = "WebStudioStaleResponseError";
  }
}

export function defaultWebStudioScope(): WebStudioScope {
  return {
    accountId: "account_unselected",
    organizationId: "org_unselected",
    projectId: "project_unselected",
    workspaceId: "workspace_unselected",
    environmentId: "environment_unselected",
    worldviewId: "worldview_default",
    lineageId: "lineage_unselected",
    activationId: "activation_unselected",
  };
}

export async function loadWebStudioBootSnapshot(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  options: WebStudioBootLoadOptions = {},
): Promise<WebStudioBootSnapshot> {
  if (options.signal?.aborted) {
    throw new WebStudioStaleResponseError("Web Studio boot request was superseded before dispatch");
  }
  const target = scopeToTarget(scope);
  const response = await client.webStudioBoot(
    makeWebStudioRequest("webStudioBoot", target, {
      query: routeStateQuery(scope),
    }),
  );
  assertCurrentBootResponse(response, options);
  return response;
}

export async function loadFactsPlane(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  options: WebStudioBootLoadOptions & { bootSnapshot?: WebStudioBootSnapshot } = {},
): Promise<WebStudioFactsPlane> {
  const bootSnapshot =
    options.bootSnapshot ?? (await loadWebStudioBootSnapshot(client, scope, options));
  assertCurrentBootResponse(bootSnapshot, options);
  return bootSnapshot.data.factsPlane;
}

/** Backend-owned billing overview rows (`lobase-billing-self-serve`
 * 7.1): the page renders exactly what the facade computed from the
 * billing cache — plan, status, entitlement axes, product usage. */
export async function loadBillingOverview(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
): Promise<WebStudioBillingOverview> {
  const target = scopeToTarget(scope);
  return client.readBillingOverview(
    makeWebStudioRequest("readBillingOverview", target, {
      query: routeStateQuery(scope),
    }),
  );
}

/**
 * Backend-owned session/cache classification for a boot snapshot. The
 * browser forwards backend-provided compatibility, staleness, and
 * authorization fields; it never derives those judgments locally.
 */
export function classifyBootSnapshot(
  boot: WebStudioBootSnapshot,
): WebStudioCachedStateClassification {
  // The primary typed diagnostic the backend attached to this boot snapshot;
  // auth-shaped classification requires a `web_studio.auth.*` code here.
  const diagnosticCode =
    boot.diagnostics[0]?.diagnostic.code ??
    boot.data.routeState?.diagnostic?.code ??
    boot.data.authorizedRouteState?.diagnostic?.code;
  // The live contract emits `routeState`; `authorizedRouteState` is the
  // legacy fixture-era spelling kept as a fallback.
  const routeState = boot.data.routeState ?? boot.data.authorizedRouteState;
  return classifyWebStudioCachedState({
    offline: false,
    // Fail closed: missing staleness reads as stale, and a missing route
    // state classifies as unauthorized rather than assuming access.
    stale: boot.compatibility.stale ?? true,
    contractVersion: boot.compatibility.contractVersion,
    ...(routeState ? { authorizationState: routeState.authorizationState } : {}),
    ...(diagnosticCode !== undefined ? { diagnosticCode } : {}),
  });
}

export function scopeToTarget(scope: WebStudioScope): WebStudioTarget {
  return {
    accountId: scope.accountId,
    organizationId: scope.organizationId,
    projectId: scope.projectId,
    workspaceId: scope.workspaceId,
    environmentId: scope.environmentId,
    ...(scope.lineageId !== undefined ? { lineageId: scope.lineageId } : {}),
    ...(scope.activationId !== undefined ? { activationId: scope.activationId } : {}),
  };
}

export function routeStateQuery(scope: WebStudioScope): Record<string, string | undefined> {
  return {
    shellReference: scope.shellReference,
    inspectorContext: scope.inspectorContext,
    activityLens: scope.activityLens,
    replaySubMode: scope.replaySubMode,
    exportKind: scope.exportKind,
    provenanceSeedId: scope.provenanceSeedId,
    commandId: scope.commandId,
    supportSnapshotId: scope.supportSnapshotId,
    mutationContextDigest: scope.mutationContextDigest,
  };
}

function assertCurrentBootResponse(
  response: WebStudioBootSnapshot,
  options: WebStudioBootLoadOptions,
): void {
  if (options.signal?.aborted) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot response was superseded before render",
      response.responseIdentity,
    );
  }
  const bootContract = webStudioCloudApiOperations.webStudioBoot.operationContract;
  if (response.operationContract !== bootContract) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot response contract does not match the generated client",
      response.responseIdentity,
    );
  }
  if (response.responseIdentity.operationContractIdentity !== bootContract) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot response identity has the wrong operation contract",
      response.responseIdentity,
    );
  }
  if (response.freshness.generatedContractIdentity !== response.operationContract) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot freshness metadata has the wrong generated contract identity",
      response.responseIdentity,
    );
  }
  if (response.cachePolicy.cacheControl !== "no-store") {
    throw new WebStudioStaleResponseError(
      "Web Studio boot response is not protected by no-store cache policy",
      response.responseIdentity,
    );
  }
  if (response.cachePolicy.serviceWorkerCacheable || response.cachePolicy.sharedCacheable) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot response is cacheable by a non-authoritative browser cache",
      response.responseIdentity,
    );
  }
  assertExpectedIdentity(
    response.responseIdentity,
    "generatedScopeDigest",
    options.expectedGeneratedScopeDigest,
  );
  assertExpectedIdentity(
    response.responseIdentity,
    "routeStateDigest",
    options.expectedRouteStateDigest,
  );
  assertExpectedIdentity(
    response.responseIdentity,
    "operationContractIdentity",
    options.expectedOperationContractIdentity,
  );
  assertExpectedIdentity(
    response.responseIdentity,
    "snapshotIdentity",
    options.expectedSnapshotIdentity,
  );
  if (
    response.data.responseIdentity.snapshotIdentity !== response.responseIdentity.snapshotIdentity
  ) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot data identity does not match the response identity",
      response.responseIdentity,
    );
  }
  if (response.data.freshness.routeStateDigest !== response.freshness.routeStateDigest) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot data freshness does not match the response freshness",
      response.responseIdentity,
    );
  }
  const expiry = Date.parse(response.freshness.expiresAt);
  if (!Number.isNaN(expiry) && expiry <= (options.now?.() ?? new Date()).getTime()) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot snapshot has expired",
      response.responseIdentity,
    );
  }
  if (options.isCurrent && !options.isCurrent(response)) {
    throw new WebStudioStaleResponseError(
      "Web Studio boot response no longer matches the active browser request",
      response.responseIdentity,
    );
  }
}

function assertExpectedIdentity(
  identity: WebStudioResponseIdentity,
  key: keyof WebStudioResponseIdentity,
  expected?: string,
): void {
  if (expected && identity[key] !== expected) {
    throw new WebStudioStaleResponseError(
      `Web Studio boot ${String(key)} does not match the active browser request`,
      identity,
    );
  }
}
